import { Weekday } from "../components/Weekdays.ts";
import { User } from "oidc-client-ts";
import { doDelete, doGet, doPost, doPut } from "./util/apiHelper.ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { SCHEDULE_QUERY_KEYS } from "./util/queryKeys.ts";

export interface TimeSlot {
  id: string;
  locationId: string;
  weekday: Weekday;
  startHour: string;
  startMinute: string;
  endHour: string;
  endMinute: string;
}

export interface Schedule {
  id?: string;
  locationId: string;
  timeSlots: TimeSlot[];
}

interface TimeSlotResponse {
  dayOfWeek: Weekday;
  timeZone: string;
  from: string;
  to: string;
}

interface ScheduleResponse {
  id: string;
  locationId: string;
  timeSlots: TimeSlotResponse[];
}

interface SchduleListResponse {
  schedules: ScheduleResponse[];
}

interface UpdateScheduleRequest {
  locationId: string;
  timeSlots: TimeSlotResponse[];
}

export type InstantRange = {
  from: Date;
  to: Date;
};

export interface OneTimeSlotsListResponse {
  oneTimeSlots: OneTimeSlotsResponse[];
}

export type OneTimeSlot = InstantRange & {
  id?: string;
  locationId: string;
};

export type OneTimeSlotsRequest = OneTimeSlot;

export type OneTimeSlotsResponse = InstantRange & {
  id: string;
  locationId: string;
};

export type Blocker = InstantRange & {
  id?: string;
  title?: string;
};

export type BlockerRequest = Blocker;

export type BlockerResponse = InstantRange & {
  id: string;
  title?: string;
};

export interface EventListResponse {
  blockers: BlockerResponse[];
}

async function getTimeslots(user: User): Promise<Schedule[]> {
  const response = await doGet<SchduleListResponse>(
    `/users/${user.profile.sub}/schedules`,
    user,
  );

  return response.schedules.map((schedule) => {
    const timeslots = schedule.timeSlots.map((timeSlotResponse): TimeSlot => {
      return {
        id: window.crypto.randomUUID(),
        locationId: schedule.locationId,
        weekday: timeSlotResponse.dayOfWeek,
        startHour: timeSlotResponse.from.split(":")[0],
        startMinute: timeSlotResponse.from.split(":")[1],
        endHour: timeSlotResponse.to.split(":")[0],
        endMinute: timeSlotResponse.to.split(":")[1],
      };
    });
    return {
      id: schedule.id,
      locationId: schedule.locationId,
      timeSlots: timeslots,
    };
  });
}

async function putSchedule(
  schedule: Schedule,
  user: User,
): Promise<TimeSlot[]> {
  const request: UpdateScheduleRequest = {
    locationId: schedule.locationId,
    timeSlots: schedule.timeSlots.map((timeSlot): TimeSlotResponse => {
      return {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dayOfWeek: timeSlot.weekday,
        from: `${timeSlot.startHour}:${timeSlot.startMinute}`,
        to: `${timeSlot.endHour}:${timeSlot.endMinute}`,
      };
    }),
  };

  return doPut<TimeSlot[]>(
    `/users/${user.profile.sub}/schedules/${schedule.id}`,
    user,
    request,
  );
}

async function postSchedule(
  schedule: Schedule,
  user: User,
): Promise<TimeSlot[]> {
  const request: UpdateScheduleRequest = {
    locationId: schedule.locationId,
    timeSlots: schedule.timeSlots.map((timeSlot): TimeSlotResponse => {
      return {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        dayOfWeek: timeSlot.weekday,
        from: `${timeSlot.startHour}:${timeSlot.startMinute}`,
        to: `${timeSlot.endHour}:${timeSlot.endMinute}`,
      };
    }),
  };

  return doPost<TimeSlot[]>(
    `/users/${user.profile.sub}/schedules`,
    user,
    request,
  );
}

async function getOneTimeSlots(user: User): Promise<OneTimeSlotsListResponse> {
  return doGet<OneTimeSlotsListResponse>(
    `/users/${user.profile.sub}/one-time-slots`,
    user,
  );
}

async function deleteOneTimeSlot(
  oneTimeSlotsRequest: OneTimeSlotsRequest,
  user: User,
): Promise<OneTimeSlotsResponse> {
  await doDelete(
    `/users/${user.profile.sub}/one-time-slots/${oneTimeSlotsRequest.id}`,
    user,
  );

  return { ...oneTimeSlotsRequest } as OneTimeSlotsResponse;
}

async function postOneTimeSlot(
  scheduleOverwriteRequest: OneTimeSlotsRequest,
  user: User,
): Promise<OneTimeSlotsResponse> {
  return doPost<OneTimeSlotsResponse>(
    `/users/${user.profile.sub}/one-time-slots`,
    user,
    scheduleOverwriteRequest,
  );
}

async function getBlocker(user: User): Promise<EventListResponse> {
  return doGet<EventListResponse>(`/users/${user.profile.sub}/blocker`, user);
}

async function deleteBlocker(
  eventRequest: BlockerRequest,
  user: User,
): Promise<BlockerResponse> {
  await doDelete(`/users/${user.profile.sub}/blocker/${eventRequest.id}`, user);

  return { ...eventRequest } as BlockerResponse;
}

async function postBlocker(
  blockerRequest: BlockerRequest,
  user: User,
): Promise<BlockerResponse> {
  return doPost<BlockerResponse>(
    `/users/${user.profile.sub}/blocker`,
    user,
    blockerRequest,
  );
}

export function useSchedule() {
  return useAuthenticatedQuery(SCHEDULE_QUERY_KEYS.schedule, getTimeslots);
}

export function useScheduleMutation() {
  return useAuthenticatedMutation(
    (schedule: Schedule, user: User) => {
      if (schedule.id) {
        return putSchedule(schedule, user);
      }
      return postSchedule(schedule, user);
    },
    { invalidateKeys: [SCHEDULE_QUERY_KEYS.schedule] },
  );
}

export function useOneTimeSlots() {
  return useAuthenticatedQuery(
    SCHEDULE_QUERY_KEYS.oneTimeSlots,
    getOneTimeSlots,
  );
}

export function useOneTimeSlotMutation() {
  return useAuthenticatedMutation(
    (scheduleOverwrite: OneTimeSlot, user: User) => {
      if (scheduleOverwrite.id) {
        return deleteOneTimeSlot(scheduleOverwrite, user);
      }
      return postOneTimeSlot(scheduleOverwrite, user);
    },
    { invalidateKeys: [SCHEDULE_QUERY_KEYS.oneTimeSlots] },
  );
}

export function useBlocker() {
  return useAuthenticatedQuery(SCHEDULE_QUERY_KEYS.blocker, getBlocker);
}

export function useBlockerMutation() {
  return useAuthenticatedMutation(
    (event: Blocker, user: User) => {
      if (event.id) {
        return deleteBlocker(event, user);
      }
      return postBlocker(event, user);
    },
    { invalidateKeys: [SCHEDULE_QUERY_KEYS.blocker] },
  );
}
